/**
 * entry-links.tsx — the row of "open this" buttons under a schedule slot.
 *
 * One renderer, used by the Schedule (where the links are made) and by Today
 * (where they are used). They looked the same when they were written twice, and
 * would not have stayed that way: a kind added to
 * migrations/0064_schedule_entry_links.sql has one place to land here.
 */
import type * as React from 'react'
import { ClipboardCheck, Dices, PenTool } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { linkKindOf, linkPath, linkTitle } from '@/lib/schedule-links'
import type { ScheduleEntryLinkKind, ScheduleEntryLinkWithTarget } from '@/lib/types'
import { cn } from '@/lib/utils'

export const LINK_ICON: Record<ScheduleEntryLinkKind, React.ComponentType<{ className?: string }>> = {
  board: PenTool,
  evaluation: ClipboardCheck,
  game: Dices,
}

export function EntryLinks({
  links,
  className,
}: {
  links: ScheduleEntryLinkWithTarget[]
  className?: string
}) {
  if (links.length === 0) return null
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {links.map((link) => {
        const kind = linkKindOf(link)
        const to = linkPath(link)
        // A row with no target cannot exist through the API (0064's check
        // constraint), and a pill that goes nowhere is worse than no pill.
        if (!kind || !to) return null
        const Icon = LINK_ICON[kind]
        return (
          <Button key={link.id} asChild variant="outline" size="sm">
            <Link to={to}>
              <Icon /> {linkTitle(link)}
              {/* An archived board still opens — it is linked because the team
                  meant to use it — but saying so saves the "why is this empty"
                  minute. */}
              {kind === 'board' && link.boards?.archived && (
                <Badge variant="muted" className="ml-1 text-[10px]">
                  archived
                </Badge>
              )}
            </Link>
          </Button>
        )
      })}
    </div>
  )
}
